import React, { useState } from 'react';
import { 
  Settings, 
  Bell, 
  Database, 
  Wifi, 
  Moon, 
  Sun, 
  Monitor,
  Save,
  RotateCcw
} from 'lucide-react';
import NotificationSettings from '../components/NotificationSettings';

interface ThemeSettings {
  mode: 'light' | 'dark' | 'system';
}

interface MQTTSettings {
  brokerUrl: string;
  username: string;
  password: string;
  clientId: string;
  reconnectInterval: number;
}

interface DatabaseSettings {
  retentionDays: number;
  maxHistorySize: number;
  autoBackup: boolean;
  backupInterval: string;
}

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'general' | 'notifications' | 'mqtt' | 'database'>('general');
  const [theme, setTheme] = useState<ThemeSettings>({ mode: 'system' });
  const [mqttSettings, setMqttSettings] = useState<MQTTSettings>({
    brokerUrl: 'mqtt://37.60.236.60:1883',
    username: '',
    password: '',
    clientId: 'agricultural-iot-frontend',
    reconnectInterval: 5000
  });
  const [dbSettings, setDbSettings] = useState<DatabaseSettings>({
    retentionDays: 30,
    maxHistorySize: 10000,
    autoBackup: true,
    backupInterval: 'daily'
  });

  const tabs = [
    { id: 'general' as const, label: 'Général', icon: Settings },
    { id: 'notifications' as const, label: 'Notifications', icon: Bell },
    { id: 'mqtt' as const, label: 'MQTT', icon: Wifi },
    { id: 'database' as const, label: 'Base de Données', icon: Database }
  ];

  const handleThemeChange = (mode: 'light' | 'dark' | 'system') => {
    setTheme({ mode });
    
    // Appliquer le thème
    if (mode === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (mode === 'light') {
      document.documentElement.classList.remove('dark');
    } else {
      // Mode système
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
    
    localStorage.setItem('theme', mode);
  };

  const saveSettings = () => {
    // Sauvegarder les paramètres
    localStorage.setItem('mqttSettings', JSON.stringify(mqttSettings));
    localStorage.setItem('dbSettings', JSON.stringify(dbSettings));
    
    // Afficher un message de confirmation
    alert('Paramètres sauvegardés avec succès !');
  };

  const resetSettings = () => {
    if (confirm('Êtes-vous sûr de vouloir réinitialiser tous les paramètres ?')) {
      // Réinitialiser aux valeurs par défaut
      setTheme({ mode: 'system' });
      setMqttSettings({
        brokerUrl: 'mqtt://37.60.236.60:1883',
        username: '',
        password: '',
        clientId: 'agricultural-iot-frontend',
        reconnectInterval: 5000
      });
      setDbSettings({
        retentionDays: 30,
        maxHistorySize: 10000,
        autoBackup: true,
        backupInterval: 'daily'
      });
      
      // Nettoyer le localStorage
      localStorage.removeItem('theme');
      localStorage.removeItem('mqttSettings');
      localStorage.removeItem('dbSettings');
      localStorage.removeItem('notificationSettings');
      
      alert('Paramètres réinitialisés !');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Paramètres
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Configurez votre système IoT agricole selon vos besoins
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          {/* Onglets */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Contenu des onglets */}
          <div className="p-6">
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Apparence
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-700 dark:text-gray-300 min-w-[100px]">Thème:</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleThemeChange('light')}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${
                            theme.mode === 'light'
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                          }`}
                        >
                          <Sun className="w-4 h-4" />
                          Clair
                        </button>
                        <button
                          onClick={() => handleThemeChange('dark')}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${
                            theme.mode === 'dark'
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                          }`}
                        >
                          <Moon className="w-4 h-4" />
                          Sombre
                        </button>
                        <button
                          onClick={() => handleThemeChange('system')}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${
                            theme.mode === 'system'
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                          }`}
                        >
                          <Monitor className="w-4 h-4" />
                          Système
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Informations Système
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Version:</span>
                      <span className="text-gray-900 dark:text-white">2.0.0</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Dernière mise à jour:</span>
                      <span className="text-gray-900 dark:text-white">{new Date().toLocaleDateString('fr-FR')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Navigateur:</span>
                      <span className="text-gray-900 dark:text-white">{navigator.userAgent.split(' ')[0]}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <NotificationSettings />
            )}

            {activeTab === 'mqtt' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Configuration MQTT
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL du Broker
                      </label>
                      <input
                        type="text"
                        value={mqttSettings.brokerUrl}
                        onChange={(e) => setMqttSettings(prev => ({ ...prev, brokerUrl: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="mqtt://broker.example.com:1883"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Nom d'utilisateur
                        </label>
                        <input
                          type="text"
                          value={mqttSettings.username}
                          onChange={(e) => setMqttSettings(prev => ({ ...prev, username: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Mot de passe
                        </label>
                        <input
                          type="password"
                          value={mqttSettings.password}
                          onChange={(e) => setMqttSettings(prev => ({ ...prev, password: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          ID Client
                        </label>
                        <input
                          type="text"
                          value={mqttSettings.clientId}
                          onChange={(e) => setMqttSettings(prev => ({ ...prev, clientId: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Intervalle de reconnexion (ms)
                        </label>
                        <input
                          type="number"
                          min="1000"
                          max="60000"
                          value={mqttSettings.reconnectInterval}
                          onChange={(e) => setMqttSettings(prev => ({ ...prev, reconnectInterval: parseInt(e.target.value) }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'database' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Configuration Base de Données
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Rétention des données (jours)
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="365"
                          value={dbSettings.retentionDays}
                          onChange={(e) => setDbSettings(prev => ({ ...prev, retentionDays: parseInt(e.target.value) }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Taille max historique
                        </label>
                        <input
                          type="number"
                          min="1000"
                          max="100000"
                          value={dbSettings.maxHistorySize}
                          onChange={(e) => setDbSettings(prev => ({ ...prev, maxHistorySize: parseInt(e.target.value) }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-gray-900 dark:text-white font-medium">Sauvegarde automatique</span>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Activer les sauvegardes automatiques</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={dbSettings.autoBackup}
                          onChange={(e) => setDbSettings(prev => ({ ...prev, autoBackup: e.target.checked }))}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Fréquence de sauvegarde
                      </label>
                      <select
                        value={dbSettings.backupInterval}
                        onChange={(e) => setDbSettings(prev => ({ ...prev, backupInterval: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="hourly">Toutes les heures</option>
                        <option value="daily">Quotidienne</option>
                        <option value="weekly">Hebdomadaire</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Boutons d'action */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between">
            <button
              onClick={resetSettings}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Réinitialiser
            </button>
            
            <button
              onClick={saveSettings}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Save className="w-4 h-4" />
              Sauvegarder
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
